import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  formatDate,
  FormField,
  List,
  Stack,
  Stepper,
  SummaryStat,
  SummaryStats,
  Text,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { toDate, toIso } from '../payHistory/PayHistoryView';
import {
  acceptedCount,
  Col,
  FilePicker,
  plural,
  SentFilesTable,
  useSendFiles,
  type Sent,
} from './SentFiles';

/**
 * Upload files to pending pay (pc-dbb.14): a right drawer in three steps. Step 1 sets the
 * pending pay date (fixed while pending pay is loaded); step 2 takes the files and sends
 * each one with the date; step 3 confirms them, with the date large, and follows each
 * one as it's processed.
 */

export const PENDING_UPLOAD_STEPS = ['Pay date', 'Files', 'Queued'] as const;

export interface PendingUploadDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The pending pay date while pending pay is loaded: the date can't change here. */
  lockedDate: string | null;
  /** A rejected upload's error report. */
  onOpenErrorReport: (upload: { id: string; fileName: string }) => void;
}

/** The pending Upload files drawer. Remount it (a `key`) each time it opens. */
export function PendingUploadDrawer({
  open,
  onClose,
  lockedDate,
  onOpenErrorReport,
}: PendingUploadDrawerProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [payDate, setPayDate] = useState<string | null>(lockedDate);
  const [files, setFiles] = useState<File[]>([]);
  const [sent, setSent] = useState<Sent[] | null>(null);
  const { send, busy } = useSendFiles();

  const upload = async () => {
    if (!payDate) return;
    setSent(await send(files, 'pending', payDate));
    setStep(3);
  };

  const footer =
    step === 1 ? (
      <>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => setStep(2)} disabled={!payDate}>
          Continue
        </Button>
      </>
    ) : step === 2 ? (
      <>
        <Button variant="secondary" onClick={() => setStep(1)} disabled={busy}>
          Back
        </Button>
        <Button
          onClick={() => void upload()}
          disabled={files.length === 0}
          loading={busy}
          loadingText="Uploading…"
        >
          Upload files
        </Button>
      </>
    ) : (
      <Button onClick={onClose}>Done</Button>
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="md"
      title="Upload files"
      description={<Stepper steps={PENDING_UPLOAD_STEPS} current={step} />}
      dismissable={!busy}
      footer={footer}
    >
      {step === 1 && (
        <FormField
          label="Pending pay date"
          hint={
            lockedDate
              ? `Adding to pending pay for ${formatDate(lockedDate)}. To use a different date, clear pending pay first.`
              : 'The date these payments are expected.'
          }
        >
          <DatePicker
            value={payDate ? toDate(payDate) : null}
            onChange={(date) => setPayDate(date ? toIso(date) : null)}
            disabled={lockedDate !== null}
            allowTextInput
          />
        </FormField>
      )}

      {step === 2 && (
        <Stack gap={4}>
          <FilePicker files={files} onFilesChange={setFiles} disabled={busy} />
          <Alert tone="info" title="How files are read">
            <List>
              <li>
                Rows are matched to users by <Col>PAYEE EMAIL</Col>.
              </li>
              <li>
                {payDate
                  ? `${formatDate(payDate)} applies to every row in every file uploaded here.`
                  : 'The pending pay date applies to every row in every file uploaded here.'}
              </li>
              <li>Uploading adds to the pending pay; it doesn’t replace it.</li>
              <li>
                A file with any invalid row is rejected in full. Other files in the same upload
                still process.
              </li>
            </List>
          </Alert>
        </Stack>
      )}

      {step === 3 && payDate && sent && (
        <Stack gap={5}>
          <SummaryStats aria-label="Queued">
            <SummaryStat label="Pending pay date" value={formatDate(payDate)} size="lg" />
            <SummaryStat label="Files queued" value={acceptedCount(sent)} />
          </SummaryStats>
          <Text as="p">
            {acceptedCount(sent) === sent.length
              ? `${plural(sent.length, 'file is', 'files are')} validating and importing in the background. You can close this panel and keep working.`
              : `${acceptedCount(sent)} of ${plural(sent.length, 'file', 'files')} queued: the others weren’t uploaded. The rest are validating and importing in the background.`}
          </Text>
          <SentFilesTable sent={sent} onOpenErrorReport={onOpenErrorReport} />
        </Stack>
      )}
    </Drawer>
  );
}
